import { useRef } from 'react'
import { Link } from 'react-router-dom'

import { useAuthStore } from '@/shared/session'
import { useSettingsStore } from '@/entities/settings'
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/ui'

/**
 * Asked once, ever: locally by default, or once per account once signed in
 * (the store mirrors the answer to the cloud the same way it does `animations`).
 * No close button and no dismissing it by clicking outside — a definitive
 * answer is the point, so only the two buttons (or Escape) count as one.
 *
 * Waits for `cloudSettingsChecked` (set once `loadSettingsFromCloud` — already
 * called elsewhere on sign-in — settles) before deciding to ask a signed-in
 * visitor, so a new device for an already-answered account doesn't flash the
 * dialog open first. Doesn't call `loadSettingsFromCloud` itself: that would
 * duplicate the request App.tsx already makes.
 */
export default function AnimationsPrompt() {
  const authLoading = useAuthStore((s) => s.loading)
  const user = useAuthStore((s) => s.user)
  const animationsPromptSeen = useSettingsStore((s) => s.animationsPromptSeen)
  const answerAnimationsPrompt = useSettingsStore((s) => s.answerAnimationsPrompt)
  const cloudSettingsChecked = useSettingsStore((s) => s.cloudSettingsChecked)
  // Base UI would otherwise focus the "Settings" link first, as the first
  // focusable element — this is one of the two answers, not a footnote.
  const noButtonRef = useRef<HTMLButtonElement>(null)

  const ready = !authLoading && (!user || cloudSettingsChecked)

  return (
    <Dialog
      open={ready && !animationsPromptSeen}
      disablePointerDismissal
      onOpenChange={(next) => {
        if (!next) answerAnimationsPrompt(false)
      }}
    >
      <DialogContent initialFocus={noButtonRef}>
        <DialogHeader showCloseButton={false}>
          <DialogTitle>Turn on animations?</DialogTitle>
          <DialogDescription>
            Tome of Many Faces can animate menus, dialogs and the character wizard as you move
            through them. They're off by default.{' '}
            <Link to="/settings/preferences">Settings → Preferences</Link> has the switch if you
            change your mind later.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button ref={noButtonRef} variant="outline" onClick={() => answerAnimationsPrompt(false)}>
            No, keep them off
          </Button>
          <Button onClick={() => answerAnimationsPrompt(true)}>
            Yes, enable them
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
